import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

export type LeiResult = Record<string, unknown>;

export interface LeiLookupContextType {
  leiTarget: string | null;
  jobId: string | null;
  leiResult: LeiResult | null;
  leiSessionId: string | null;
  lookupsRemaining: number | null;
  isLeiUnlimited: boolean;
  setLeiTarget: Dispatch<SetStateAction<string | null>>;
  setLeiJobId: Dispatch<SetStateAction<string | null>>;
  setLeiResult: Dispatch<SetStateAction<LeiResult | null>>;
  setLeiSessionId: Dispatch<SetStateAction<string | null>>;
  setLookupsRemaining: Dispatch<SetStateAction<number | null>>;
  setIsLeiUnlimited: Dispatch<SetStateAction<boolean>>;
  resetLeiLookup: () => void;
}

const LeiLookupContext = createContext<LeiLookupContextType | undefined>(undefined);

export const LeiLookupProvider = ({ children }: { children: ReactNode }) => {
  const [leiTarget, setLeiTarget] = useState<string | null>(null);
  const [jobId, setLeiJobId] = useState<string | null>(null);
  const [leiResult, setLeiResult] = useState<LeiResult | null>(null);
  const [leiSessionId, setLeiSessionId] = useState<string | null>(null);
  const [lookupsRemaining, setLookupsRemaining] = useState<number | null>(null);
  const [isLeiUnlimited, setIsLeiUnlimited] = useState<boolean>(false);

  const resetLeiLookup = () => {
    setLeiTarget(null);
    setLeiJobId(null);
    setLeiResult(null);
    setLeiSessionId(null);
    setLookupsRemaining(null);
    setIsLeiUnlimited(false);
  };

  const value = useMemo(
    () => ({
      leiTarget,
      jobId,
      leiResult,
      leiSessionId,
      lookupsRemaining,
      isLeiUnlimited,
      setLeiTarget,
      setLeiJobId,
      setLeiResult,
      setLeiSessionId,
      setLookupsRemaining,
      setIsLeiUnlimited,
      resetLeiLookup,
    }),
    [leiTarget, jobId, leiResult, leiSessionId, lookupsRemaining, isLeiUnlimited]
  );

  return <LeiLookupContext.Provider value={value}>{children}</LeiLookupContext.Provider>;
};

export const useLeiLookup = (): LeiLookupContextType => {
  const context = useContext(LeiLookupContext);
  if (context === undefined) {
    throw new Error('useLeiLookup must be used within a LeiLookupProvider');
  }
  return context;
};

